import { analytics, date as dateUtil } from '@eti/utils';
import { useSiteContext } from '@eti/providers';
import minBy from 'lodash/minBy';
import sumBy from 'lodash/sumBy';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getAgesOfChildren, getAgesOfInfants } from '../../utils/format';
import { VIRTUAL_INTERLINING } from '../../constants/tripCharacteristics';
import { routes } from '../../constants/routesAndApisConstants';
import type { SearchData } from '../../pages/result/models';
import { RESPONSIVE_PAGEVIEW_DETAILS } from '../../constants/dataLayerEventsConstants';
import { getUrlFor } from '../routing/utils/url';
import type {
  BookingStepQueryBoundsType,
  BookingStepQueryPaymentMethodPricesType,
  BookingStepQuerySegmentsType,
  BookingStepQuerySelectedTripType,
  BookingStepQueryTravelerPricesType,
} from '../types/queries/getBookingStepPageDataQuery/models';

export const getPageTypeDetails = (pathname?: string) => {
  const routeName =
    (pathname &&
      Object.entries(routes).find((route) => route[1] === pathname.replace('/rf', ''))?.[0]) ??
    'IMPRESSUM';

  return (
    (routeName &&
      RESPONSIVE_PAGEVIEW_DETAILS[routeName as keyof typeof RESPONSIVE_PAGEVIEW_DETAILS]) || {
      page_type: 'Other',
      page_type_details: 'Other',
    }
  );
};

export const getResponsivePageViewEvent = (
  pathname?: string,
  pageType?: string,
  pageTypeDetailed?: string,
  pageLocation?: string,
) => {
  const pageTypeDetails = getPageTypeDetails(pathname);
  const referrer = sessionStorage.getItem('documentReferrer') || document.referrer;
  sessionStorage.setItem('documentReferrer', window.location.href);
  const scenarios =
    window && window.spa_variables && 'scenarios' in window.spa_variables
      ? window.spa_variables.scenarios
      : [];

  return {
    event: 'responsive_pageview',
    responsive_url: pathname || window.location.pathname,
    page_location: pageLocation || window.location.href,
    page_title: document.title,
    page_type: pageType || pageTypeDetails.page_type,
    page_type_detailed: pageTypeDetailed || pageTypeDetails.page_type_details,
    page_referrer: referrer,
    privacy_policy_url: getUrlFor(routes.PRIVACY_POLICY).href,
    scenarios,
  };
};

export const usePageViewDataLayerEvent = (dataLayerPathname?: string) => {
  const { pathname } = useLocation();

  const responsiveUrl = dataLayerPathname || pathname;

  useEffect(() => {
    setTimeout(
      () => analytics.pushEntryToDataLayer(getResponsivePageViewEvent(responsiveUrl)),
      2000,
    );
  }, [responsiveUrl]);
};

export const useSearchValuesToDataLayerEffect = (currentSearchData: SearchData | undefined) => {
  useEffect(() => {
    if (currentSearchData?.routes) {
      const firstRoute = currentSearchData.routes[0];
      const departureDate = (currentSearchData.date && currentSearchData.date.departure) ?? '';
      const returnDate = (currentSearchData.date && currentSearchData.date.return) ?? '';

      analytics.pushEntryToDataLayer(
        {
          event: 'searchEvent',
          childrenAges:
            (currentSearchData.travelers && currentSearchData.travelers.childAges) ?? [],
          city: firstRoute.destinationCity,
          cityCode: firstRoute.destinationCityCode,
          continent: firstRoute.destinationContinent,
          continentCode: firstRoute.destinationContinentCode,
          country: firstRoute.destinationCountry,
          destination: firstRoute.destinationName,
          destinationCountry: firstRoute.destinationCountry,
          destinationCountryCode: firstRoute.destinationCountryCode,
          nrOfAdults: (currentSearchData.travelers && currentSearchData.travelers.adults) ?? 0,
          nrOfChildren: getAgesOfChildren(
            (currentSearchData.travelers && currentSearchData.travelers.childAges) ?? [],
          ).length,
          nrOfInfants: getAgesOfInfants(
            (currentSearchData.travelers && currentSearchData.travelers.childAges) ?? [],
          ).length,
          origin: firstRoute.originName,
          originCity: firstRoute.originCity,
          originCityCode: firstRoute.originCityCode,
          originCountry: firstRoute.originCountry,
          originCountryCode: firstRoute.originCountryCode,
          originContinent: firstRoute.originContinent,
          originContinentCode: firstRoute.originContinentCode,
          startDate: departureDate,
          startDateDay: dateUtil.format(departureDate, 'dd'),
          startDateMonth: dateUtil.format(departureDate, 'MM'),
          startDateYear: dateUtil.format(departureDate, 'yyyy'),
          startDateYearMonth: dateUtil.format(departureDate, 'yyyy-MM'),
          startDay: dateUtil.format(departureDate, 'iii'),
          endDate: returnDate,
          endDateDay: dateUtil.format(returnDate, 'dd'),
          endDateMonth: dateUtil.format(returnDate, 'MM'),
          endDateYear: dateUtil.format(returnDate, 'yyyy'),
          endDateYearMonth: dateUtil.format(returnDate, 'yyyy-MM'),
          endDay: dateUtil.format(returnDate, 'iii'),
        },
        false,
      );
    }
  }, [currentSearchData]);
};

const tripSegmentsFilter = (segment: BookingStepQuerySegmentsType[number]) =>
  segment.__typename === 'TripSegment';

const getAirportCodes = (bounds: BookingStepQueryBoundsType) => {
  const tripSegmentsOnFirstBound = bounds[0].segments.filter(tripSegmentsFilter);
  const { origin } = tripSegmentsOnFirstBound[0];
  const { destination } = tripSegmentsOnFirstBound[tripSegmentsOnFirstBound.length - 1];

  const originAirportCode = origin.isAirport ? origin.code : null;
  const destinationAirportCode = destination.isAirport ? destination.code : null;

  return { originAirportCode, destinationAirportCode };
};

export const useSelectedTripToDataLayerEffect = (
  selectedTrip: BookingStepQuerySelectedTripType,
) => {
  useEffect(() => {
    if (selectedTrip) {
      const allTripSegments = selectedTrip.bounds.flatMap(
        (bound) => bound.segments?.filter(tripSegmentsFilter) ?? [],
      );

      const uniqueMarketingCarrierCodes = Array.from(
        new Set(
          allTripSegments.map((segment) =>
            'marketingCarrier' in segment ? segment.marketingCarrier.code : undefined,
          ),
        ),
      );
      const uniqueOperatingCarrierCodes = Array.from(
        new Set(
          allTripSegments.map((segment) =>
            'operatingCarrier' in segment ? segment.operatingCarrier.code : undefined,
          ),
        ),
      );
      const uniqueMarketingCarrierNames = Array.from(
        new Set(
          allTripSegments.map((segment) =>
            'marketingCarrier' in segment ? segment.marketingCarrier.name : undefined,
          ),
        ),
      );
      const uniqueTicketingCarrierCodes = Array.from(
        new Set(
          allTripSegments.reduce((acc: string[], segment) => {
            const ticketingCarrierCode = segment.ticketingCarrier?.code ?? null;
            return ticketingCarrierCode ? [...acc, ticketingCarrierCode] : acc;
          }, []),
        ),
      );

      const cabinClassNames = selectedTrip.bounds
        .flatMap(
          (bound) =>
            bound.segments?.filter(
              (segment) => 'cabinClassName' in segment && segment.cabinClassName,
            ) ?? [],
        )
        .map((tripSegment) =>
          'cabinClassName' in tripSegment ? tripSegment.cabinClassName : undefined,
        );

      const { originAirportCode, destinationAirportCode } = getAirportCodes(selectedTrip.bounds);

      if (
        selectedTrip.tripCharacteristics &&
        selectedTrip.tripCharacteristics.includes(VIRTUAL_INTERLINING)
      ) {
        analytics.pushEntryToDataLayer({ event: 'VITrip' }, false);
      }

      analytics.pushEntryToDataLayer(
        {
          event: 'selectedTripEvent',
          cabinClass: cabinClassNames,
          airBoundXMarketingCarrier: uniqueMarketingCarrierCodes,
          airBoundXOperatingCarrier: uniqueOperatingCarrierCodes,
          airBoundXTicketingCarrier: uniqueTicketingCarrierCodes,
          carrier: uniqueMarketingCarrierNames,
          carrierCode: uniqueMarketingCarrierCodes,
          destinationAirportCode,
          originAirportCode,
        },
        false,
      );
    }
  }, [selectedTrip]);
};

export const useTravelerPricesToDataLayerEffect = (
  travelerPrices: BookingStepQueryTravelerPricesType = [],
  paymentMethodPrices: BookingStepQueryPaymentMethodPricesType = [],
) => {
  const siteContext = useSiteContext();

  useEffect(() => {
    if (siteContext && travelerPrices.length > 0) {
      const totalPrice =
        sumBy(travelerPrices, 'price.price.value') / 10 ** siteContext.siteCurrency.exponent;

      analytics.pushEntryToDataLayer({
        event: 'priceEvent',
        price: totalPrice,
      });

      if (siteContext.partner && !analytics.keyExistsInDataLayer('priceSeen')) {
        const minByPrice = minBy(paymentMethodPrices, 'price.value');
        analytics.pushEntryToDataLayer(
          {
            event: 'priceSeenEvent',
            priceSeen: totalPrice,
            priceSeenPaymentMethod:
              (minByPrice && 'type' in minByPrice && minByPrice?.type) ?? null,
          },
          false,
        );
      }
    }
  }, [travelerPrices, paymentMethodPrices, siteContext]);
};
